import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/shared/utils';

interface DetailCardProps {
  /** Icon to display in the card header */
  icon?: LucideIcon;
  /** Card title */
  title: string;
  /** Header action (e.g. button or link) */
  headerAction?: ReactNode;
  /** Card content */
  children: ReactNode;
  /** Whether to remove padding from content area (useful for tables) */
  noPadding?: boolean;
  className?: string;
}

/**
 * Detail card component matching the prototype design.
 * Features a header with icon and title on muted background.
 */
export function DetailCard({
  icon: Icon,
  title,
  headerAction,
  children,
  noPadding,
  className,
}: DetailCardProps) {
  return (
    <section
      className={cn(
        'bg-card border border-border rounded-xl shadow-sm overflow-hidden transition-all duration-300',
        className
      )}
    >
      <div className="px-6 py-4 border-b border-border bg-muted flex justify-between items-center">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={18} className="text-primary" />}
          <h3 className="font-semibold text-foreground">{title}</h3>
        </div>
        {headerAction}
      </div>
      <div className={cn(noPadding ? 'p-0' : 'p-6')}>{children}</div>
    </section>
  );
}

interface DetailCardSimpleProps {
  /** Icon to display next to the title */
  icon?: LucideIcon;
  /** Card title */
  title: string;
  /** Card content */
  children: ReactNode;
  className?: string;
}

/**
 * Simple detail card without header background.
 * Used for sidebar cards like "License & Quota".
 */
export function DetailCardSimple({
  icon: Icon,
  title,
  children,
  className,
}: DetailCardSimpleProps) {
  return (
    <section
      className={cn(
        'bg-card border border-border rounded-xl shadow-sm p-6 transition-colors duration-300',
        className
      )}
    >
      <div className="flex items-center gap-2 mb-6">
        {Icon && <Icon size={18} className="text-primary" />}
        <h3 className="font-semibold text-foreground">{title}</h3>
      </div>
      {children}
    </section>
  );
}

interface InfoCardProps {
  /** Card title */
  title: string;
  /** Card content */
  children: ReactNode;
  /** Variant for different styling */
  variant?: 'info' | 'warning' | 'success';
  className?: string;
}

/**
 * Info/hint card with colored background.
 */
export function InfoCard({ title, children, variant = 'info', className }: InfoCardProps) {
  const variantStyles = {
    info: 'bg-info-subtle border-info-subtle text-info-subtle-foreground',
    warning: 'bg-warning-subtle border-warning-subtle text-warning-subtle-foreground',
    success: 'bg-success-subtle border-success-subtle text-success-subtle-foreground',
  };

  return (
    <section className={cn('border rounded-xl p-6', variantStyles[variant], className)}>
      <h3 className="font-semibold mb-2">{title}</h3>
      <div className="text-sm opacity-80 leading-relaxed">{children}</div>
    </section>
  );
}
